import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { PlayerAuctionService } from './player-auction.service';
import { SellContractRow, ContractStatus, CONTRACT_STATUS_OPTIONS, SellContractStatsReport } from './player-auction.type';
import { MuleDialog } from './mule-dialog/mule-dialog';
import { SellContractFormDialog } from './sell-contract-form-dialog/sell-contract-form-dialog';
import { ContractCardsDialog } from './contract-cards-dialog/contract-cards-dialog';
import { SellContractLogDialog } from './sell-contract-log-dialog/sell-contract-log-dialog';
import { ShareLinkDialog } from '../shared/components/share-link-dialog/share-link-dialog';
import { PatchContractDialog } from './patch-contract-dialog/patch-contract-dialog';
import { SellContractInfoDialog } from './sell-contract-info-dialog/sell-contract-info-dialog';
import { BillingEditDialog } from './billing-edit-dialog/billing-edit-dialog';
import { OutsourcingHandoffDialog } from './outsourcing-handoff-dialog/outsourcing-handoff-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { Header } from '../shared/header/header';
import { ContractBalanceWidget } from './contract-balance/contract-balance';
import { ContractPageNav } from './contract-page-nav/contract-page-nav';
import { Clipboard } from '@angular/cdk/clipboard';
import { debounceTime, distinctUntilChanged, EMPTY, fromEvent, map, startWith, Subject, Subscription, switchMap, timer } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AccountList } from '../account/account-list/account-list';
import { AuthService } from '../auth/auth.service';
import { UsersService } from '../users/users.service';
import { UserRole } from '../auth/auth.type';
import { LoginInfoFormDialog } from '../login-info/login-info-form-dialog/login-info-form-dialog';
import { LoginInfo } from '../login-info/login-info.type';

@Component({
  selector: 'app-player-auction',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    MatInputModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSidenavModule,
    AccountList,
    Header,
    ContractBalanceWidget,
    ContractPageNav,
  ],
  templateUrl: './player-auction.html',
  styleUrl: './player-auction.scss'
})
export class PlayerAuction implements OnInit {
  baseColumns = ['select', 'id', 'note', 'status', 'coins', 'target_coins', 'cards_summary', 'issue', 'timing', 'action'];

  contracts: SellContractRow[] = [];
  loading = true;
  refreshing = false;
  private hasLoadedOnce = false;
  pageIndex = 0;
  pageSize = 20;
  total = 0;
  totalPages = 0;
  lastUpdate = 0;
  /** Bumped on every reload so the page nav refreshes its counts alongside the list. */
  refreshTick = 0;
  /** Tablet/mobile only: the non-search filters stay folded behind a toggle. */
  filtersOpen = false;

  filterStatus: string = '';
  filterSearch: string = '';
  filterArchived: 'false' | 'true' | '' = 'false';
  filterInPool: 'true' | 'false' | '' = '';
  filterQuickSelling: 'true' | 'false' | '' = '';
  filterOwnerRole: string = '';
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  statusOptions: ContractStatus[] = CONTRACT_STATUS_OPTIONS;
  roleOptions: UserRole[] = [];

  /** Inline, user-fixable validation errors from the backend (400s) — shown next to the relevant filter instead of a toast. */
  searchError: string | null = null;
  dateRangeError: string | null = null;

  stats: SellContractStatsReport | null = null;
  statsLoading = false;

  /** Admins default to their own normal view; they can opt into the admin panel view. */
  viewMode: 'normal' | 'admin' = 'normal';

  selectedIds = new Set<string>();

  isMobile = window.innerWidth <= 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private searchSubject = new Subject<string>();
  private destroyRef = inject(DestroyRef);
  private clipboard = inject(Clipboard);
  /** In-flight list/stats requests; a newer load cancels the older one so slow responses can't pile up. */
  private listSub?: Subscription;
  private statsSub?: Subscription;

  constructor(
    private service: PlayerAuctionService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private usersService: UsersService,
  ) { }

  get isAdmin(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  get canEdit(): boolean {
    return this.authService.canWrite('contracts');
  }

  get useAdminView(): boolean {
    return this.isAdmin && this.viewMode === 'admin';
  }

  get displayedColumns(): string[] {
    if (!this.useAdminView) return this.baseColumns;
    const cols = [...this.baseColumns];
    cols.splice(cols.indexOf('note'), 0, 'owner');
    cols.splice(cols.indexOf('cards_summary'), 2, 'fee', 'total_price');
    return cols;
  }

  onViewModeChange(): void {
    this.pageIndex = 0;
    this.filterOwnerRole = '';
    this.loadContracts();
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.listSub?.unsubscribe();
      this.statsSub?.unsubscribe();
    });
    this.searchSubject.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.searchError) return;
      this.pageIndex = 0;
      this.loadContracts();
    });
    if (this.isAdmin) {
      this.usersService.getAllRoles().subscribe({
        next: res => this.roleOptions = res.result,
        error: () => { /* role filter is optional; ignore failures */ },
      });
    }
    this.startAutoRefresh();
  }

  startAutoRefresh(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged()
    );

    // Both streams must stop with the page: left running, every visit stacked
    // another 30 s poll and 1 s change-detection tick until the tab crawled.
    visibility$.pipe(
      switchMap(visible => visible ? timer(0, 30_000) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.loadContracts());

    visibility$.pipe(
      switchMap(visible => visible ? timer(0, 1_000) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => { this.lastUpdate++; });
  }

  onSearchChange(): void {
    // Quick client-side pre-check so obviously-broken regex gets immediate feedback
    // without waiting on a round trip; the backend (Postgres ~*) still has final say.
    if (this.filterSearch) {
      try {
        new RegExp(this.filterSearch);
        this.searchError = null;
      } catch {
        this.searchError = 'Invalid search pattern';
      }
    } else {
      this.searchError = null;
    }
    this.searchSubject.next(this.filterSearch);
  }

  /** date_from/date_to as inclusive RFC3339 bounds on created_at, shared by the list and stats-report calls. */
  private buildDateParams(): { date_from?: string; date_to?: string } {
    const params: { date_from?: string; date_to?: string } = {};
    if (this.filterStartDate) {
      params.date_from = this.filterStartDate.toISOString();
    }
    if (this.filterEndDate) {
      const endOfDay = new Date(this.filterEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      params.date_to = endOfDay.toISOString();
    }
    return params;
  }

  private extractInlineError(err: any): boolean {
    if (err?.status !== 400) return false;
    const message: string | undefined = err?.error?.error;
    if (!message) return false;
    if (/search/i.test(message)) {
      this.searchError = message;
    } else if (/date/i.test(message)) {
      this.dateRangeError = message;
    } else {
      return false;
    }
    return true;
  }

  loadContracts(): void {
    if (!this.hasLoadedOnce) {
      this.loading = true;
    }
    this.refreshing = true;
    this.refreshTick++;
    const archived = this.filterArchived === '' ? undefined : this.filterArchived === 'true';
    const quick_selling = this.filterQuickSelling === '' ? undefined : this.filterQuickSelling === 'true';
    const dateParams = this.buildDateParams();
    const request$ = this.useAdminView
      ? this.service.adminGetSellContracts({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        status: this.filterStatus as ContractStatus || undefined,
        search: this.filterSearch || undefined,
        ownerRole: this.filterOwnerRole || undefined,
        archived,
        quick_selling,
        ...dateParams,
      })
      : this.service.getSellContracts({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        status: this.filterStatus as ContractStatus || undefined,
        search: this.filterSearch || undefined,
        archived,
        in_pool: this.filterInPool === '' ? undefined : this.filterInPool === 'true',
        quick_selling,
        ...dateParams,
      });

    this.listSub?.unsubscribe();
    this.listSub = request$.subscribe({
      next: res => {
        this.contracts = res.items ?? [];
        this.total = res.total;
        this.totalPages = res.totalPages;
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
        this.lastUpdate = 0;
      },
      error: err => {
        if (!this.extractInlineError(err)) {
          this.snackBar.open('Error loading contracts', 'Close', { duration: 5000 });
        }
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
      }
    });
    this.loadStats();
  }

  loadStats(): void {
    this.statsLoading = true;
    const archived = this.filterArchived === '' ? undefined : this.filterArchived === 'true';
    this.statsSub?.unsubscribe();
    this.statsSub = this.service.getSellContractStatsReport({
      search: this.filterSearch || undefined,
      archived,
      in_pool: this.filterInPool === '' ? undefined : this.filterInPool === 'true',
      ...this.buildDateParams(),
    }).subscribe({
      next: res => {
        this.stats = res;
        this.statsLoading = false;
      },
      error: err => {
        this.extractInlineError(err);
        this.statsLoading = false;
      }
    });
  }

  onFilterChange(): void {
    this.dateRangeError = null;
    this.pageIndex = 0;
    this.loadContracts();
  }

  onDateRangeChange(): void {
    if (this.filterStartDate && this.filterEndDate && this.filterStartDate > this.filterEndDate) {
      this.dateRangeError = 'Start date must not be after end date';
      return;
    }
    this.onFilterChange();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterSearch = '';
    this.filterArchived = 'false';
    this.filterInPool = '';
    this.filterQuickSelling = '';
    this.filterOwnerRole = '';
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.searchError = null;
    this.dateRangeError = null;
    this.pageIndex = 0;
    this.loadContracts();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadContracts();
  }

  setStatusFilter(status: string): void {
    this.filterStatus = status;
    this.onFilterChange();
  }

  setViewMode(mode: 'normal' | 'admin'): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.onViewModeChange();
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterStatus || this.filterSearch || this.filterArchived !== 'false' || this.filterInPool
      || this.filterQuickSelling || this.filterOwnerRole || this.filterStartDate || this.filterEndDate);
  }

  /** Filters folded behind the toggle on small screens; search and status live outside it. */
  get hasExtraFilters(): boolean {
    return !!(this.filterArchived !== 'false' || this.filterInPool || this.filterQuickSelling || this.filterOwnerRole
      || this.filterStartDate || this.filterEndDate);
  }

  copyName(contract: SellContractRow): void {
    if (this.clipboard.copy(contract.name)) {
      this.snackBar.open('Name copied', undefined, { duration: 1500 });
    }
  }

  openAccountDialog(): void {
    this.dialog.open(MuleDialog, {
      width: window.innerWidth <= 768 ? '100vw' : undefined,
      minWidth: window.innerWidth <= 768 ? undefined : '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    });
  }

  openCreateContractDialog(): void {
    const isMobile = window.innerWidth <= 768;
    const ref = this.dialog.open(SellContractFormDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
    });
    ref.afterClosed().subscribe(result => {
      if (result) this.loadContracts();
    });
  }

  viewCards(contract: SellContractRow): void {
    this.dialog.open(ContractCardsDialog, {
      width: window.innerWidth <= 768 ? '100vw' : undefined,
      minWidth: window.innerWidth <= 768 ? undefined : '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { contractId: contract.id },
    });
  }

  viewLogs(contract: SellContractRow): void {
    this.dialog.open(SellContractLogDialog, {
      width: window.innerWidth <= 768 ? '100vw' : undefined,
      minWidth: window.innerWidth <= 768 ? undefined : '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { contractId: contract.id },
    });
  }

  resume(contract: SellContractRow): void {
    this.service.resumeSellContract(contract.id).subscribe({
      next: () => {
        this.snackBar.open('Contract resumed', 'Close', { duration: 5000 });
        this.loadContracts();
      },
      error: err => {
        this.snackBar.open(err.error?.error || 'Error resuming contract', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  shareSellerLink(contract: SellContractRow): void {
    const url = `${window.location.origin}/player-auction/${contract.id}/seller`;
    this.dialog.open(ShareLinkDialog, {
      data: { url },
      width: '420px',
      maxWidth: '95vw',
    });
  }

  shareBuyerLink(contract: SellContractRow): void {
    const url = `${window.location.origin}/pa/${contract.id}/buyer`;
    this.dialog.open(ShareLinkDialog, {
      data: { url },
      width: '420px',
      maxWidth: '95vw',
    });
  }

  openContractInfoDialog(contract: SellContractRow): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SellContractInfoDialog, {
      data: { contract },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '520px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openPatchDialog(contract: SellContractRow): void {
    const isMobile = window.innerWidth <= 768;
    const ref = this.dialog.open(PatchContractDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: '90vh',
      data: { contract },
    });
    ref.afterClosed().subscribe(result => {
      if (result) this.loadContracts();
    });
  }

  openOutsourcingHandoffDialog(contract: SellContractRow): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(OutsourcingHandoffDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '560px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
      data: { contract },
    });
  }

  openBillingDialog(contract: SellContractRow): void {
    const ref = this.dialog.open(BillingEditDialog, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        contractLabel: contract.name || this.shortId(contract.id),
        fee: contract.fee,
        payment_status: contract.payment_status,
      },
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.service.updateSellContractBilling(contract.id, result).subscribe({
        next: () => {
          this.snackBar.open('Billing updated', 'Close', { duration: 4000 });
          this.loadContracts();
        },
        error: err => {
          const msg = err.error?.error || 'Error updating billing';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
        }
      });
    });
  }

  setPriority(contract: SellContractRow, priority: boolean): void {
    this.service.updateSellContractPriority(contract.id, priority).subscribe({
      next: () => {
        this.snackBar.open(priority ? 'Contract prioritized' : 'Priority removed', 'Close', { duration: 4000 });
        this.loadContracts();
      },
      error: err => {
        const msg = err.error?.error || 'Error updating priority';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  isNotCompleted(contract: SellContractRow): boolean {
    return contract.status !== 'Done' && contract.status !== 'Canceled' && contract.status !== 'Expired';
  }

  canCancel(contract: SellContractRow): boolean {
    return this.canEdit && contract.status !== 'Done' && contract.status !== 'Canceled';
  }

  canResume(contract: SellContractRow): boolean {
    return this.canEdit && (contract.status === 'Failed' || contract.status === 'Expired' || contract.status === 'Canceled' || contract.status === 'Stopped');
  }

  canStop(contract: SellContractRow): boolean {
    return this.canEdit && contract.status === 'Transferring';
  }

  stop(contract: SellContractRow): void {
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Stop Contract',
        message: `Stop listing on "${contract.name || this.shortId(contract.id)}"? Listed cards stay monitored and the account stays locked. If it is not resumed within 3 hours it fails (or completes as Done if the target is already reached).`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.stopSellContract(contract.id).subscribe({
        next: () => {
          this.snackBar.open('Contract stopped', 'Close', { duration: 5000 });
          this.loadContracts();
        },
        error: err => {
          this.snackBar.open(err.error?.error || 'Error stopping contract', 'Close', { duration: 5000 });
        }
      });
    });
  }

  cancel(contract: SellContractRow): void {
    this.service.cancelSellContract(contract.id).subscribe({
      next: () => {
        this.snackBar.open('Contract canceled', 'Close', { duration: 5000 });
        this.loadContracts();
      },
      error: () => {
        this.snackBar.open('Error cancelling contract', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  canQuickSellLeftovers(contract: SellContractRow): boolean {
    return this.canEdit && (contract.status === 'Canceled' || contract.status === 'Failed') && !contract.quick_sell_started_at;
  }

  quickSellLeftovers(contract: SellContractRow): void {
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Quick Sell Leftovers',
        message: `Force quick sell any leftover expired cards on "${contract.name || this.shortId(contract.id)}"? Any buyer lock will be released and cards will be sold at a loss. This cannot be undone.`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.quickSellLeftovers({ ids: [contract.id] }).subscribe({
        next: () => {
          this.snackBar.open('Quick sell leftovers enqueued', 'Close', { duration: 5000 });
          this.loadContracts();
        },
        error: err => {
          const msg = err.error?.error || 'Error enqueuing quick sell leftovers';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
        }
      });
    });
  }

  bulkQuickSellLeftovers(): void {
    const ids = [...this.selectedIds];
    if (!ids.length) return;
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Quick Sell Leftovers',
        message: `Force quick sell leftover expired cards on ${ids.length} contract(s)? Any buyer lock will be released and cards will be sold at a loss. This cannot be undone.`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.quickSellLeftovers({ ids }).subscribe({
        next: () => {
          this.snackBar.open('Quick sell leftovers enqueued', 'Close', { duration: 5000 });
          this.selectedIds.clear();
          this.loadContracts();
        },
        error: err => {
          const msg = err.error?.error || 'Error enqueuing quick sell leftovers';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
        }
      });
    });
  }

  get selectedEligibleForQuickSell(): boolean {
    if (!this.selectedIds.size) return false;
    return this.contracts
      .filter(c => this.selectedIds.has(c.id))
      .every(c => this.canQuickSellLeftovers(c));
  }

  setArchived(contract: SellContractRow, archived: boolean): void {
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: archived ? 'Archive Contract' : 'Unarchive Contract',
        message: archived
          ? `Are you sure you want to archive "${contract.name || this.shortId(contract.id)}"?`
          : `Are you sure you want to unarchive "${contract.name || this.shortId(contract.id)}"?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.archiveSellContract({ ids: [contract.id], archived }).subscribe({
        next: () => {
          this.snackBar.open(archived ? 'Contract archived' : 'Contract unarchived', 'Close', { duration: 5000 });
          this.loadContracts();
        },
        error: () => this.snackBar.open('Error updating contract', 'Close', { duration: 5000 }),
      });
    });
  }

  bulkSetArchived(archived: boolean): void {
    const ids = [...this.selectedIds];
    if (!ids.length) return;
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: archived ? 'Archive Contracts' : 'Unarchive Contracts',
        message: archived
          ? `Are you sure you want to archive ${ids.length} contract(s)?`
          : `Are you sure you want to unarchive ${ids.length} contract(s)?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.archiveSellContract({ ids, archived }).subscribe({
        next: () => {
          this.snackBar.open(archived ? `${ids.length} contract(s) archived` : `${ids.length} contract(s) unarchived`, 'Close', { duration: 5000 });
          this.selectedIds.clear();
          this.loadContracts();
        },
        error: () => this.snackBar.open('Error updating contracts', 'Close', { duration: 5000 }),
      });
    });
  }

  toggleSelect(id: string): void {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
  }

  get allSelected(): boolean {
    return this.contracts.length > 0 && this.contracts.every(c => this.selectedIds.has(c.id));
  }

  get someSelected(): boolean {
    return this.selectedIds.size > 0 && !this.allSelected;
  }

  toggleSelectAll(): void {
    if (this.allSelected) this.contracts.forEach(c => this.selectedIds.delete(c.id));
    else this.contracts.forEach(c => this.selectedIds.add(c.id));
  }

  getStatusClass(status: ContractStatus): string {
    switch (status) {
      case 'Pending': return 'status-pending';
      case 'Transferring': return 'status-transferring';
      case 'Stopped': return 'status-stopped';
      case 'Outsourcing': return 'status-outsourcing';
      case 'Done': return 'status-done';
      case 'Canceled': return 'status-canceled';
      case 'Expired': return 'status-expired';
      case 'Failed': return 'status-failed';
    }
  }

  getPaymentClass(status: string): string {
    return status === 'Payed' ? 'payment-payed' : 'payment-not-payed';
  }

  getPaymentIcon(status: string): string {
    return status === 'Payed' ? 'check_circle' : 'cancel';
  }

  formatCoins(n: number): string {
    if (!n) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  }

  formatToman(n: number): string {
    if (!n) return '—';
    return n.toLocaleString('en-US');
  }

  /** Status counts in a fixed, readable order; omits statuses with no matches. */
  get statsStatusEntries(): { status: ContractStatus; count: number }[] {
    if (!this.stats) return [];
    return this.statusOptions
      .map(status => ({ status, count: this.stats!.status_counts[status] ?? 0 }))
      .filter(e => e.count > 0);
  }

  /** Combined sent+received coin flow vs. total target, across all matched contracts regardless of status or target_type. */
  get statsPercentReached(): number {
    if (!this.stats || !this.stats.total_target) return 0;
    return Math.min(100, Math.round(((this.stats.total_received) / this.stats.total_target) * 100));
  }

  formatFeeRate(rate: number): string {
    return (rate * 100).toFixed(2) + '%';
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString();
  }

  formatDateAndTime(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString() + ' ' + new Date(d).toLocaleTimeString();
  }

  shortId(id: string): string {
    return id ? id.slice(0, 4) + '…' : '—';
  }

  trackByContractId(_index: number, c: SellContractRow): string {
    return c.id;
  }

  openEditDialog(contract: SellContractRow): void {
    const loginInfo: LoginInfo = {
      id: contract.buyer_account_info?.login_info_id || '',
      email: contract.buyer_account_info?.email || '',
      password: '',
      backup_code_1: '',
      has_cookies: false,
      user_id: '',
      created_at: new Date(),
      updated_at: new Date()
    };

    const isMobile = window.innerWidth <= 768;
    this.dialog.open(LoginInfoFormDialog, {
      width: isMobile ? '100vw' : '600px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'edit', loginInfo }
    });
  }
}
